import { useEffect, useState } from "react";
import { StandaloneCouponsAPI } from "../../../providers/StandaloneCouponsAPI";
import { Button } from "../../components/Button";
import { Drawer } from "../../components/Drawer";
import { Loading } from "../../components/Loading";
import { Steps } from "../../components/Steps";
import { If } from "../../components/control-statements";

import { OlgaEnvironmentsAPI } from "../../../providers/OlgaEnvironmentsAPI";
import { Label } from "../../components/Label";
import { Radio } from "../../components/Radio";
import { BenefitInsertCodeStep, BenefitInsertCodeStepOld } from "./steps/BenefitInsertCodeStepOld";
import { BenefitSelectTypeStep, BenefitSelectTypeStepOld } from "./steps/BenefitSelectTypeStepOld";
import { BenefitShowQrCodeStep, BenefitShowQrCodeStepOld } from "./steps/BenefitShowQrCodeStepOld";
import { BenefitUseTypeStep, BenefitUseTypeStepOld } from "./steps/BenefitUseTypeStepOld";
import { BenefitEnvironment, BenefitEnvironmentOld } from "./steps/BenefitEnvironmentOld";
import BenefitVisibility from "./steps/BenefitVisibilityOld";
import ContemplateCustomers from "./steps/ContemplateCustomers";
import { Environment } from "shared/core/Environment";
import BenefitVisibilityOld from "./steps/BenefitVisibilityOld";

export const BenefitFormDrawerOld = ({ visible, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [step, setStep] = useState(1);
  const [stepsMax, setStepsMax] = useState(5);
  const [benefit, setBenefit] = useState({ contemplate: false });
  const [isValid, setIsValid] = useState(false);
  const [brands, setBrands] = useState([]);
  const [partners, setPartners] = useState([]);
  const [benefitAlwaysAvailable, setBenefitAlwaysAvailable] = useState(null);

  const getBrands = async () => {
    const response = await OlgaEnvironmentsAPI.getBrands();
    const brands = response.getData([]);
    if ((brands || []).length > 1) {
      setStepsMax(6);
    }
    setBrands(brands);
  };

  const getPartners = async () => {
    const response = await OlgaEnvironmentsAPI.getPartners();
    const partners = response.getData([]);
    setPartners(partners);
  };

  const checkTimeErrors = (visibility) => {
    const conflicts = [];

    visibility?.forEach((day) => {
      day.times.forEach((time) => {
        if (time.start === time.end || time.start >= time.end) {
          conflicts.push({
            weekDay: day.weekDay,
            conflictingTime: time,
          });
        }
      });
    });

    return conflicts.length > 0;
  };

  const validateStep = (benefit, step) => {
    switch (step) {
      case 1: {
        return !!benefitTypes[benefit.type];
      }
      case 2: {
        let isValueOk = false;
        if (
          [
            benefitTypes.PERCENTAGE_DISCOUNT,
            benefitTypes.CASHBACK,
            benefitTypes.PRODUCT_DISCOUNT,
          ].includes(benefit.type)
        ) {
          isValueOk = Number(benefit.value) > 0 && Number(benefit.value) <= 100;
        } else if (benefit.type === benefitTypes.FREE_DELIVERY) {
          isValueOk = true;
        } else {
          isValueOk = Number(benefit.value) > 0;
        }

        const isCodeOk = (benefit.code || "").length > 0;
        const isExpirationDaysOk = Number(benefit.expiration_days) > 0;
        const isSpendDaysOk = Number(benefit.spend_days) > 0;
        const isGroupOk =
          Environment.id == 34 ? (benefit.group || "").length > 0 : true;

        const isCommonOk = isCodeOk && isExpirationDaysOk && isValueOk;

        const hasSkus = benefit.skus?.length > 0;
        const hasProductName = (benefit.description || "").length > 0;

        const isCouponProductOk =
          [
            benefitTypes.PRODUCT_DISCOUNT,
            benefitTypes.PRODUCT_CASH_DISCOUNT,
          ].includes(benefit.type) &&
          hasSkus &&
          hasProductName &&
          isCommonOk;

        if (isCouponProductOk) return true;

        const isCouponOk =
          ![
            benefitTypes.CASHBACK,
            benefitTypes.PRODUCT_DISCOUNT,
            benefitTypes.PRODUCT_CASH_DISCOUNT,
          ].includes(benefit.type) && isCommonOk;
        const isCashbackOk =
          benefit.type === benefitTypes.CASHBACK && isCommonOk;
        // && isSpendDaysOk;
        return (isCouponOk || isCashbackOk) && isGroupOk;
      }
      case 3: {
        if (benefit.visibility?.length === 0) return false;

        const isVisibilityOk = !checkTimeErrors(benefit.visibility);

        return isVisibilityOk;
      }
      case 4: {
        return benefit.type_use > 0 && benefit.type_use < 16;
      }
      case 5: {
        return (brands || []).length > 1
          ? (benefit.brands_valid || []).length > 0
          : !benefit.contemplate ||
              (!!benefit.contemplate && benefit.customersPhone?.length > 0);
      }
      case 6: {
        return (
          !benefit.contemplate ||
          (!!benefit.contemplate && benefit.customersPhone?.length > 0)
        );
      }
      default:
        break;
    }

    return false;
  };

  useEffect(() => {
    getBrands();
    getPartners();
  }, []);

  const nextStep = () => {
    setStep((step) => step + 1);
    setIsValid(validateStep(benefit, step + 1));
  };

  const prevStep = () => {
    setStep((step) => step - 1);
    setIsValid(validateStep(benefit, step - 1));
  };

  const onChangeBenefit = (b) => {
    setBenefit(b);
    setIsValid(validateStep(b, step));
  };

  useEffect(() => {
    setIsValid(validateStep(benefit, step));
  }, [benefitAlwaysAvailable]);

  const saveCoupon = async () => {
    setLoading(true);

    const requestBody = {
      ...benefit,
      standalone: true,
      status: "ACTIVATED",
    };

    if (requestBody.type === "DELIVERY") {
      requestBody.value = 0;
    }

    const response =
      await StandaloneCouponsAPI.postStandaloneCoupons(requestBody);
    setLoading(false);

    const errors = response.getErrors([]);
    if (errors.length > 0) {
      return alert({ text: errors[0] });
    }

    const savedBenefit = response.getData({});
    setBenefit({ ...requestBody, pin: savedBenefit.pin });
    setShowQrCode(true);
    setStep(1);

    if (onSave) {
      onSave();
    }
  };

  const programByPoint =
    localStorage.getItem("cashbackProgramType") === "POINT";

  return (
    <Drawer
      title={getTitle(benefit?.type, showQrCode, programByPoint)}
      visible={visible}
      onClose={() => {
        onClose();
        setStep(1);
        setShowQrCode(false);
        onChangeBenefit({});
      }}
      showBackButton={step > 1 && !loading && !showQrCode}
      backButton={prevStep}
      footer={
        <If cond={!loading}>
          <If cond={step < stepsMax && !showQrCode}>
            <Button
              design="primary"
              disabled={!isValid}
              className={"w-100"}
              onClick={nextStep}
            >
              Próximo
            </Button>
          </If>
          <If cond={step === stepsMax}>
            <Button
              design="primary"
              disabled={!isValid}
              className={"w-100"}
              onClick={saveCoupon}
            >
              Concluir e gerar QR Code
            </Button>
          </If>
          <If cond={showQrCode}>
            <Button
              className={"w-100"}
              design="primary"
              onClick={() => {
                onClose();
                setStep(1);
                setShowQrCode(false);
                onChangeBenefit({});
              }}
            >
              Voltar para listagem de cupons
            </Button>
          </If>
        </If>
      }
    >
      <If cond={loading}>
        <div className="d-flex align-center justify-center h-100 w-100">
          <Loading />
        </div>
      </If>

      <If cond={showQrCode}>
        <BenefitShowQrCodeStepOld benefit={benefit?.code ? benefit : null} />
      </If>

      <If cond={!loading && !showQrCode}>
        <Steps steps={(brands || []).length > 0 ? 6 : 5} currentStep={step} />
        <If cond={step === 1}>
          <BenefitSelectTypeStepOld
            benefit={benefit}
            onChange={(b) => onChangeBenefit(b)}
            programByPoint={programByPoint}
          />
        </If>
        <If cond={step === 2}>
          <BenefitInsertCodeStepOld
            benefit={benefit}
            partners={partners}
            onChange={(b) => onChangeBenefit(b)}
            programByPoint={programByPoint}
          />
        </If>
        <If cond={step === 3}>
          <BenefitVisibilityOld
            benefit={benefit}
            onChange={(b) => onChangeBenefit(b)}
          />
        </If>
        <If cond={step === 4}>
          <BenefitUseTypeStepOld
            benefit={benefit}
            onChange={(b) => onChangeBenefit(b)}
          />
        </If>
        <If cond={step === 5 && (brands || []).length > 1}>
          <BenefitEnvironmentOld
            benefit={benefit}
            brands={brands}
            onChange={(b) => onChangeBenefit(b)}
          />
        </If>
        <If cond={(brands || []).length > 1 ? step === 6 : step === 5}>
          <Label>Gostaria de contemplar clientes?</Label>
          <Radio
            family="contemplation"
            className="mb-15"
            value={false}
            checked={benefit.contemplate}
            onChange={() => onChangeBenefit({ ...benefit, contemplate: false })}
          >
            Não
          </Radio>
          <Radio
            family="contemplation"
            value={true}
            checked={benefit.contemplate}
            onChange={() => onChangeBenefit({ ...benefit, contemplate: true })}
          >
            Sim, gostaria
          </Radio>

          {!!benefit.contemplate && (
            <div className="mt-20">
              <ContemplateCustomers
                embeddedComponent
                onUpdateCustomerList={(customers) => {
                  onChangeBenefit({ ...benefit, customersPhone: customers });
                }}
              />
            </div>
          )}
        </If>
      </If>
    </Drawer>
  );
};

const benefitTypes = {
  CASH_DISCOUNT: "CASH_DISCOUNT",
  PERCENTAGE_DISCOUNT: "PERCENTAGE_DISCOUNT",
  FREE_DELIVERY: "FREE_DELIVERY",
  CASHBACK: "CASHBACK",
  PRODUCT_DISCOUNT: "PRODUCT_DISCOUNT",
  PRODUCT_CASH_DISCOUNT: "PRODUCT_CASH_DISCOUNT",
};

const getTitle = (type, showQrCode, programByPoint) => {
  if (showQrCode) {
    return "Cupom avulso criado com sucesso!";
  }

  if (type) {
    let title = "";
    switch (type) {
      case benefitTypes.CASH_DISCOUNT:
        title = "- Desconto em R$";
        break;
      case benefitTypes.PERCENTAGE_DISCOUNT:
        title = "- Desconto em %";
        break;
      case benefitTypes.FREE_DELIVERY:
        title = "- Entrega grátis";
        break;
      case benefitTypes.CASHBACK:
        title = programByPoint ? "- Acréscimo de pontos" : "- Cashback";
        break;
      case benefitTypes.PRODUCT_DISCOUNT:
      case benefitTypes.PRODUCT_CASH_DISCOUNT:
        title = "- Produto";
        break;
      default:
        break;
    }

    return `Criar benefício ${title}`;
  }

  return "Criar benefício";
};
